import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';

// ── OTP Digit Input ────────────────────────────────────────────────────
// Six individual single-character inputs that auto-advance on entry and
// auto-retreat on backspace. Paste of a 6-digit string is also handled.
const OtpInput = ({ value, onChange }) => {
  const inputs = useRef([]);

  const handleChange = (index, e) => {
    const char = e.target.value.replace(/\D/g, '').slice(-1); // digits only
    const next = value.split('');
    next[index] = char;
    onChange(next.join(''));
    if (char && index < 5) {
      inputs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      if (value[index]) {
        // Clear the current cell
        const next = value.split('');
        next[index] = '';
        onChange(next.join(''));
      } else if (index > 0) {
        // Move to previous cell
        inputs.current[index - 1]?.focus();
      }
    }
    if (e.key === 'ArrowLeft' && index > 0) inputs.current[index - 1]?.focus();
    if (e.key === 'ArrowRight' && index < 5) inputs.current[index + 1]?.focus();
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    onChange(pasted.padEnd(6, '').slice(0, 6));
    const focusIndex = Math.min(pasted.length, 5);
    inputs.current[focusIndex]?.focus();
  };

  return (
    <div className="flex gap-2 justify-center">
      {Array.from({ length: 6 }).map((_, i) => (
        <input
          key={i}
          ref={(el) => (inputs.current[i] = el)}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={value[i] || ''}
          onChange={(e) => handleChange(i, e)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={handlePaste}
          className={`w-11 h-12 text-center text-lg font-semibold rounded-lg border transition focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
            value[i]
              ? 'border-blue-400 bg-blue-50 text-blue-900'
              : 'border-gray-200 bg-gray-50 text-gray-900'
          }`}
          aria-label={`Digit ${i + 1}`}
        />
      ))}
    </div>
  );
};

// ── Resend Countdown ───────────────────────────────────────────────────
// Shows a countdown timer; enables the Resend button only when it hits 0.
const ResendButton = ({ onResend }) => {
  const [seconds, setSeconds] = useState(60);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (seconds <= 0) return;
    const id = setTimeout(() => setSeconds((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [seconds]);

  const handleResend = async () => {
    setResending(true);
    try {
      await onResend();
      toast.success('A new code has been sent to your email.');
      setSeconds(60); // reset countdown
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not resend code.');
    } finally {
      setResending(false);
    }
  };

  if (seconds > 0) {
    return (
      <p className="text-xs text-gray-400 text-center">
        Resend code in <span className="font-medium text-gray-600">{seconds}s</span>
      </p>
    );
  }

  return (
    <button
      type="button"
      onClick={handleResend}
      disabled={resending}
      className="text-xs text-blue-600 hover:text-blue-700 transition font-medium disabled:opacity-50 w-full text-center"
    >
      {resending ? 'Sending…' : 'Resend code'}
    </button>
  );
};

// ── Signup Page ────────────────────────────────────────────────────────

const Signup = () => {
  const locationState = useLocation().state || {};

  const [step, setStep] = useState(locationState.step === 'verify' ? 'verify' : 'register');
  const [name, setName] = useState('');
  const [email, setEmail] = useState(locationState.email || '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // OTP state
  const [otp, setOtp] = useState('');
  const [verifying, setVerifying] = useState(false);

  const { register, verifyEmail, resendOtp } = useAuth();
  const navigate = useNavigate();

  // ── Step 1: Register ────────────────────────────────────────────────
  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // register() now returns { requiresVerification: true, email } — it
      // does NOT issue a JWT. The OTP email is sent server-side.
      await register(name, email, password);
      setStep('verify');
      toast.info('Check your email for a 6-digit verification code.');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not create account');
    } finally {
      setLoading(false);
    }
  };

  // ── Step 2: Verify OTP ──────────────────────────────────────────────
  const handleVerify = async (e) => {
    e.preventDefault();
    if (otp.length < 6) {
      toast.error('Please enter all 6 digits.');
      return;
    }
    setVerifying(true);
    try {
      // verifyEmail() verifies the OTP, stores the JWT, and sets the user
      // in AuthContext — completing the login.
      await verifyEmail(email, otp);
      toast.success('Email verified! Welcome to MV Computers.');
      navigate('/customer/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Verification failed. Please try again.');
    } finally {
      setVerifying(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">

        {/* Header */}
        <div className="mb-8 text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-600 rounded-xl mb-4">
            <span className="text-white text-xl">🖨️</span>
          </div>
          <AnimatePresence mode="wait">
            {step === 'register' ? (
              <motion.div
                key="reg-header"
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.2 }}
              >
                <h1 className="text-2xl font-semibold text-gray-900">Create your account</h1>
                <p className="text-sm text-gray-500 mt-1">Book services faster and track your repairs</p>
              </motion.div>
            ) : (
              <motion.div
                key="verify-header"
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.2 }}
              >
                <h1 className="text-2xl font-semibold text-gray-900">Verify your email</h1>
                <p className="text-sm text-gray-500 mt-1">
                  We sent a 6-digit code to <span className="font-medium text-gray-700">{email}</span>
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Card */}
        <AnimatePresence mode="wait">
          {step === 'register' ? (
            <motion.div
              key="register-card"
              initial={{ opacity: 0, x: -24 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -24 }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
              className="bg-white border border-gray-100 rounded-xl shadow-sm p-6 flex flex-col gap-4"
            >
              <form onSubmit={handleRegister} className="flex flex-col gap-4">

                {/* Name */}
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Full name <span className="text-orange-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    required
                    className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  />
                </div>

                {/* Email */}
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Email <span className="text-orange-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  />
                </div>

                {/* Password */}
                <div className="flex flex-col gap-1">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Password <span className="text-orange-500">*</span>
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="text-xs text-blue-500 hover:text-blue-700 transition"
                    >
                      {showPassword ? 'Hide' : 'Show'}
                    </button>
                  </div>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 8 chars (A-z, 0-9, @$!%*?&#)"
                    required
                    minLength={8}
                    className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  />
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium py-3 rounded-xl transition mt-1"
                >
                  {loading ? 'Creating account…' : 'Continue'}
                </button>

              </form>

              {/* Divider */}
              <div className="flex items-center gap-3 my-1">
                <div className="flex-1 h-px bg-gray-100" />
                <span className="text-xs text-gray-400">or</span>
                <div className="flex-1 h-px bg-gray-100" />
              </div>

              {/* Login link */}
              <p className="text-center text-sm text-gray-500">
                Already have an account?{' '}
                <Link to="/login" className="text-blue-600 font-medium hover:text-blue-700 transition">
                  Sign in
                </Link>
              </p>

            </motion.div>
          ) : (
            <motion.div
              key="verify-card"
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 24 }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
              className="bg-white border border-gray-100 rounded-xl shadow-sm p-6 flex flex-col gap-5"
            >
              {/* Envelope icon */}
              <div className="flex items-center justify-center">
                <div className="w-14 h-14 rounded-full bg-blue-50 flex items-center justify-center text-2xl">
                  ✉️
                </div>
              </div>

              <form onSubmit={handleVerify} className="flex flex-col gap-5">
                <div className="flex flex-col gap-3">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide text-center">
                    Enter 6-digit code
                  </p>
                  <OtpInput value={otp} onChange={setOtp} />
                </div>

                <button
                  type="submit"
                  disabled={verifying || otp.length < 6}
                  className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium py-3 rounded-xl transition"
                >
                  {verifying ? 'Verifying…' : 'Verify email'}
                </button>
              </form>

              {/* Resend */}
              <ResendButton onResend={() => resendOtp(email)} />

              {/* Wrong email? */}
              <button
                type="button"
                onClick={() => { setStep('register'); setOtp(''); }}
                className="text-xs text-gray-400 hover:text-gray-600 transition text-center"
              >
                ← Use a different email
              </button>

            </motion.div>
          )}
        </AnimatePresence>

        {/* Engineer application — separate, admin-approved flow, not a signup option */}
        {step === 'register' && (
          <p className="text-center text-xs text-gray-400 mt-5">
            Want to work as a repair engineer?{' '}
            <Link to="/apply-engineer" className="text-blue-600 hover:underline">
              Apply here
            </Link>
          </p>
        )}
      </div>
    </div>
  );
};

export default Signup;